import {
  ListenerEffectAPI,
  PayloadAction,
  createListenerMiddleware,
} from '@reduxjs/toolkit';
import { actions } from '../slices';
import api from '../api';
import { MessageType } from '@law-connect/types';
import selectors from '../selectors';
import { AppDispatch, RootState } from '../store';

// long poll to server to fetch updated messages based off connectionId
let activeId: string | null = null;
const lawyerMessageMiddleware = createListenerMiddleware();
let isLongPolling = false;

lawyerMessageMiddleware.startListening({
  actionCreator: actions.lawyerConnection.pollMessages,
  effect: async (
    action: PayloadAction<{
      connectionId: string;
      init?: boolean;
      errorCount?: number;
    }>,
    listenerApi: ListenerEffectAPI<RootState, AppDispatch>
  ) => {
    const { connectionId, init } = action.payload;
    // if it is the first call we want to set the activeId
    if (init) {
      activeId = connectionId;
    }
    if (connectionId !== activeId || isLongPolling) {
      // we have either moved onto a new connection or the connection has been closed
      return;
    }
    try {
      isLongPolling = true;
      // we want to continously call server to get new messages for the connection that is set up
      const newMessage = await api.lawyerConnection.longPoll({ connectionId });
      isLongPolling = false;
      if (newMessage) {
        if (newMessage.type === MessageType.Refresh) {
          // Reload the connection and files
          listenerApi.dispatch(
            actions.lawyerConnection.fetchById({ id: connectionId })
          );
          listenerApi.dispatch(actions.file.fetch({ connectionId }));
        } else {

          if (newMessage.fileIds && newMessage.fileIds.length > 0) {
            listenerApi.dispatch(actions.file.fetch({ connectionId }));
          }

          const state = listenerApi.getState() as RootState;
          const messages =
            selectors.lawyerConnection.getMessagesById(connectionId)(state);
          if (newMessage && !messages.find((m) => m.id === newMessage.id)) {
            if(newMessage.type !== MessageType.EmailOpened){
              listenerApi.dispatch(
                actions.lawyerConnection.addMessage({
                  connectionId,
                  message: newMessage,
                })
              );
            } else {
              listenerApi.dispatch(
                actions.lawyerConnection.updateEmailOpenedAt({
                  connectionId,
                  emailOpenedAt: Date.now(),
                })
              );
            }
          }

          if (newMessage.type === MessageType.Closed) {
            // when we receive a closed message we want to fetch new connection info and then stop polling 
            listenerApi.dispatch(
              actions.lawyerConnection.fetchById({ id: connectionId })
            );
            return;
          }

          // when we poll messages, we also want to update the read at if message comes from lawyer
          if (newMessage.from === 'client') {
            // if the new message is from the client and is type permission and the permissions are allowed
            // we want to refetch the user and connection
            if (
              newMessage.type === MessageType.Permission &&
              newMessage.content.permissions.userDetails
            ) {
              listenerApi.dispatch(
                actions.lawyerConnection.fetchUser({ connectionId })
              );
              listenerApi.dispatch(
                actions.file.fetch({ connectionId })
              );
              listenerApi.dispatch(
                actions.lawyerConnection.fetchById({ id: connectionId })
              );
            }
          } else if (newMessage.from === 'lawyer' && newMessage.readAt) {
            // Message must exist, it has been returned with the readAt value
            // Therefore update it.
            listenerApi.dispatch(
              actions.lawyerConnection.addMessage({
                connectionId,
                message: newMessage,
              })
            );
          }
        }
      }

      // we want to cycle through the messages as long as the connection is active
      if (activeId === connectionId) {
        listenerApi.dispatch(
          actions.lawyerConnection.pollMessages({ connectionId })
        );
      }

      if(action.payload.errorCount > 0){
        // if success after error we want to fetch all the messages
        listenerApi.dispatch(
          actions.lawyerConnection.fetchMessages({ id: connectionId })
        );
      }
    } catch (error) {
      // if for some reason the connection is closed we want to fetch all the messages and retry polling up
      isLongPolling = false;
      await new Promise(r => setTimeout(r, 5000));
      await listenerApi.dispatch(
        actions.lawyerConnection.fetchMessages({ id: connectionId })
      );

      listenerApi.dispatch(
        actions.lawyerConnection.pollMessages({
          connectionId,
          errorCount: action.payload.errorCount
            ? action.payload.errorCount + 1
            : 1,
        })
      );
    }
    // dispatch actions.lawyerConnection.pollMessages again as long as activeId is set
  },
});

lawyerMessageMiddleware.startListening({
  actionCreator: actions.lawyerConnection.stopPollMessages,
  effect: async (
    action: PayloadAction<{
      connectionId: string;
    }>,
    listenerApi
  ) => {
    const { connectionId } = action.payload;
    // if it is the first call we want to set the activeId
    if (connectionId === activeId) {
      activeId = null;
    }
  },
});

export default lawyerMessageMiddleware;
